/**
 * 職業マッチングサービス
 * 診断結果と職業データをマッチングするロジック
 */

import { calculateCosineSimilarity } from './assessmentService';
import type {
  AssessmentScores,
  RIASECProfile,
  WorkValues,
  PortableSkills,
  BasicAbilities,
} from './assessmentService';

export interface CareerData {
  id: string;
  name: string;
  category: string;
  description: string;
  riasecProfile: RIASECProfile;
  requiredValues: string[];
  requiredSkills: string[];
  basicAbilities: {
    teamworkLevel: number;
    goalAchievementLevel: number;
    mentalToughnessLevel: number;
    growthMindsetLevel: number;
    leadershipLevel: number;
  };
  athleteFriendly: boolean;
  careerPath: string;
  averageSalary: string;
  requiredEducation: string;
  growthPotential: string;
}

export interface MatchResult {
  career: CareerData;
  matchScore: number;
  riasecSimilarity: number;
  valueSimilarity: number;
  skillSimilarity: number;
  basicAbilitySimilarity: number;
  reason: string;
  nextSteps: string[];
}

/**
 * RIASECプロファイルの類似度を計算
 */
function calculateRIASECSimilarity(
  userProfile: RIASECProfile,
  careerProfile: RIASECProfile
): number {
  const similarity = calculateCosineSimilarity(userProfile, careerProfile);
  return similarity * 100; // 0-100のスコアに変換
}

/**
 * 価値観の適合度を計算
 */
function calculateValueSimilarity(
  userValues: WorkValues,
  careerRequiredValues: string[]
): number {
  // ユーザーの価値観をスコアの高い順にソート
  const userValueEntries = Object.entries(userValues).sort((a, b) => b[1] - a[1]);
  const topUserValues = userValueEntries.slice(0, 3).map(([key]) => key);

  // 価値観のマッピング（日本語 → 英語キー）
  const valueMapping: { [key: string]: string } = {
    '経済的報酬': 'economicReward',
    '安定性': 'stability',
    'ワークライフバランス': 'workLifeBalance',
    '社会貢献': 'socialContribution',
    '自己成長': 'selfGrowth',
    '人間関係': 'relationships',
    '独立性': 'independence',
    '達成感': 'achievement',
  };

  // キャリアの必要価値観を英語キーに変換
  const careerValueKeys = careerRequiredValues.map(v => valueMapping[v]).filter(Boolean);

  // 一致する価値観の数をカウント
  const matchCount = topUserValues.filter(v => careerValueKeys.includes(v)).length;

  // 一致率を計算（最大3つ一致で100点）
  return (matchCount / 3) * 100;
}

/**
 * スキルの適合度を計算
 */
function calculateSkillSimilarity(
  userSkills: PortableSkills,
  careerRequiredSkills: string[]
): number {
  // ユーザーのスキルをスコアの高い順にソート
  const userSkillEntries = Object.entries(userSkills).sort((a, b) => b[1] - a[1]);
  const topUserSkills = userSkillEntries.slice(0, 4).map(([key]) => key);

  // スキルのマッピング（日本語 → 英語キー）
  const skillMapping: { [key: string]: string } = {
    'コミュニケーション力': 'communication',
    'プレゼンテーション力': 'presentation',
    '交渉力': 'negotiation',
    '傾聴力': 'listening',
    '問題発見力': 'problemIdentification',
    '問題解決力': 'problemIdentification',
    '分析力': 'analysis',
    '計画力': 'planning',
    '実行力': 'execution',
  };

  // キャリアの必要スキルを英語キーに変換
  const careerSkillKeys = careerRequiredSkills.map(s => skillMapping[s]).filter(Boolean);

  // 一致するスキルの数をカウント
  const matchCount = topUserSkills.filter(s => careerSkillKeys.includes(s)).length;

  // 一致率を計算（最大4つ一致で100点）
  return (matchCount / 4) * 100;
}

/**
 * 社会人基礎能力の適合度を計算
 */
function calculateBasicAbilitySimilarity(
  userAbilities: BasicAbilities,
  careerAbilities: CareerData['basicAbilities']
): number {
  // 各能力のスコアを5段階に正規化（20点満点を5段階に）
  const normalizeScore = (score: number) => Math.min(Math.ceil(score / 4), 5);

  const userNormalized = {
    teamwork: normalizeScore(userAbilities.teamwork),
    goalAchievement: normalizeScore(userAbilities.goalAchievement),
    mentalToughness: normalizeScore(userAbilities.mentalToughness),
    growthMindset: normalizeScore(userAbilities.growthMindset),
    leadership: normalizeScore(userAbilities.leadership),
  };

  // 各能力の差分を計算
  const differences = [
    Math.abs(userNormalized.teamwork - careerAbilities.teamworkLevel),
    Math.abs(userNormalized.goalAchievement - careerAbilities.goalAchievementLevel),
    Math.abs(userNormalized.mentalToughness - careerAbilities.mentalToughnessLevel),
    Math.abs(userNormalized.growthMindset - careerAbilities.growthMindsetLevel),
    Math.abs(userNormalized.leadership - careerAbilities.leadershipLevel),
  ];

  // 平均差分を計算（0に近いほど適合）
  const avgDifference = differences.reduce((sum, diff) => sum + diff, 0) / differences.length;

  // 差分を0-100のスコアに変換（差分0=100点、差分5=0点）
  return Math.max(0, 100 - (avgDifference / 5) * 100);
}

/**
 * 総合マッチングスコアを計算
 */
function calculateMatchScore(
  riasecSimilarity: number,
  valueSimilarity: number,
  skillSimilarity: number,
  basicAbilitySimilarity: number,
  athleteFriendly: boolean
): number {
  // 重み付け
  let score =
    riasecSimilarity * 0.4 +
    valueSimilarity * 0.25 +
    skillSimilarity * 0.25 +
    basicAbilitySimilarity * 0.1;

  // アスリート特化職業にボーナス
  if (athleteFriendly) {
    score += 5;
  }

  return Math.min(100, Math.round(score));
}

/**
 * マッチング理由を生成
 */
function generateMatchReason(
  career: CareerData,
  riasecSimilarity: number,
  valueSimilarity: number,
  skillSimilarity: number
): string {
  const reasons: string[] = [];

  if (riasecSimilarity >= 80) {
    reasons.push('あなたの興味・関心と非常に合致しています');
  } else if (riasecSimilarity >= 60) {
    reasons.push('あなたの興味・関心と合致しています');
  }

  if (valueSimilarity >= 70) {
    reasons.push('重視する価値観が一致しています');
  }

  if (skillSimilarity >= 70) {
    reasons.push('あなたの強みスキルが活かせます');
  }

  if (career.athleteFriendly) {
    reasons.push('アスリート経験が直接的に活かせる職業です');
  }

  return reasons.join('。') + '。';
}

/**
 * 次のステップを生成
 */
function generateNextSteps(career: CareerData): string[] {
  const steps: string[] = [];

  // 必要な資格がある場合
  if (career.requiredEducation && career.requiredEducation !== '不問') {
    steps.push(`必要な学歴・資格: ${career.requiredEducation}`);
  }

  // キャリアパスを提示
  steps.push(`キャリアパス: ${career.careerPath}`);

  // 業界研究を推奨
  steps.push(`${career.category}業界の研究を深める`);

  // 必要なスキルの習得
  if (career.requiredSkills.length > 0) {
    steps.push(`必要なスキル: ${career.requiredSkills.join('、')}`);
  }

  return steps;
}

/**
 * 職業マッチングを実行
 */
export function matchCareers(
  scores: AssessmentScores,
  careers: CareerData[]
): MatchResult[] {
  const results: MatchResult[] = careers.map(career => {
    const riasecSimilarity = calculateRIASECSimilarity(
      scores.riasecProfile,
      career.riasecProfile
    );
    const valueSimilarity = calculateValueSimilarity(scores.workValues, career.requiredValues);
    const skillSimilarity = calculateSkillSimilarity(
      scores.portableSkills,
      career.requiredSkills
    );
    const basicAbilitySimilarity = calculateBasicAbilitySimilarity(
      scores.basicAbilities,
      career.basicAbilities
    );

    const matchScore = calculateMatchScore(
      riasecSimilarity,
      valueSimilarity,
      skillSimilarity,
      basicAbilitySimilarity,
      career.athleteFriendly
    );

    const reason = generateMatchReason(career, riasecSimilarity, valueSimilarity, skillSimilarity);
    const nextSteps = generateNextSteps(career);

    return {
      career,
      matchScore,
      riasecSimilarity,
      valueSimilarity,
      skillSimilarity,
      basicAbilitySimilarity,
      reason,
      nextSteps,
    };
  });

  // マッチスコアの高い順にソート
  return results.sort((a, b) => b.matchScore - a.matchScore);
}

/**
 * TOP N件のマッチング結果を取得
 */
export function getTopMatches(
  scores: AssessmentScores,
  careers: CareerData[],
  topN: number = 10
): MatchResult[] {
  const allMatches = matchCareers(scores, careers);
  return allMatches.slice(0, topN);
}
